import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, catchError, filter, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { PersonProfileSidebarComponent } from '../components/person-profile-sidebar.component';
import { Person } from '../models/person.model';
import { PersonEvent } from '../models/person-event.model';
import { PersonService } from '../services/person.service';
import { PersonEventService } from '../services/person-event.service';
import { PersonEventsContextService } from './person-events-shell-page/person-events-context.service';

@Component({
  selector: 'app-person-profile-shell-page',
  standalone: true,
  imports: [CommonModule, RouterOutlet, MatIconModule, MatProgressSpinnerModule, PersonProfileSidebarComponent],
  providers: [PersonEventsContextService],
  template: `
    <div class="person-profile-shell">
      <div *ngIf="isLoading" class="loading-container"><mat-spinner diameter="40"></mat-spinner></div>
      <div *ngIf="errorMessage && !isLoading" class="error-container" role="alert">
        <mat-icon color="warn">error</mat-icon><p>{{ errorMessage }}</p>
      </div>
      <div *ngIf="!isLoading && !errorMessage && person" class="person-profile-layout">
        <app-person-profile-sidebar
          [person]="person"
          [age]="calculateAge(person.dateOfBirth)"
          [events]="events"
          (statusChanged)="reloadPerson()"
          (homeAttendanceChanged)="reloadPerson()">
        </app-person-profile-sidebar>
        <main class="person-profile-content"><div class="person-profile-route"><router-outlet></router-outlet></div></main>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; min-height: 0; }
    .person-profile-shell { height: 100%; min-height: 0; box-sizing: border-box; padding: clamp(12px, 2dvh, 24px); background: #EBF9FF; }
    .person-profile-layout { display: grid; grid-template-columns: 300px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); align-items: stretch; gap: 16px; width: 100%; height: 100%; min-height: 0; }
    .person-profile-content { display: flex; flex-direction: column; width: 100%; height: 100%; min-width: 0; min-height: 0; overflow: hidden; }
    .person-profile-route { display: flex; flex-direction: column; flex: 1; min-width: 0; min-height: 0; overflow: hidden; }
    .loading-container { display: flex; justify-content: center; align-items: center; min-height: 240px; }
    .error-container { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 240px; color: #d32f2f; }
    .error-container p { margin: 0; }
    @media (max-width: 1200px) { .person-profile-layout { grid-template-columns: 260px minmax(0, 1fr); gap: 20px; } }
    @media (max-width: 900px) {
      .person-profile-shell { padding: 12px; overflow: hidden; }
      .person-profile-layout { grid-template-columns: minmax(0, 1fr); grid-template-rows: auto minmax(0, 1fr); gap: 12px; }
    }
  `],
})
export class PersonProfileShellPageComponent implements OnInit, OnDestroy {
  person: Person | null = null;
  events: PersonEvent[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly personService: PersonService,
    private readonly personEventService: PersonEventService,
    private readonly eventsContext: PersonEventsContextService,
  ) {}

  ngOnInit(): void {
    this.eventsContext.person$.pipe(
      filter((person): person is Person => person !== null),
      switchMap(person => this.personEventService.getEvents(person.id).pipe(
        catchError(() => of([] as PersonEvent[])),
        map(events => ({ person, events })),
      )),
      takeUntil(this.destroy$),
    ).subscribe(({ person, events }) => {
      this.person = person;
      this.events = events;
      this.cdr.markForCheck();
    });
    this.route.parent?.paramMap.pipe(
      switchMap(params => {
        const personId = params.get('person-id');
        if (!personId) {
          this.errorMessage = 'No se pudo abrir la pantalla solicitada.';
          this.isLoading = false;
          return of(null);
        }

        this.isLoading = true;
        this.errorMessage = null;
        return forkJoin({
          person: this.eventsContext.initialize(personId),
          events: this.personEventService.getEvents(personId).pipe(catchError(() => of([] as PersonEvent[]))),
        });
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: result => {
        if (!result) return;
        this.person = result.person;
        this.events = result.events;
        this.breadcrumbService.setDynamicCrumb('unit-id', result.person.communityName);
        this.breadcrumbService.setDynamicCrumb('person-id', `${result.person.firstName} ${result.person.lastName}`);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'No se pudo cargar la persona.';
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  calculateAge(dateOfBirth: Date | undefined): number | undefined {
    if (!dateOfBirth || isNaN(dateOfBirth.getTime())) return undefined;
    const today = new Date();
    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) age--;
    return Math.max(0, age);
  }

  reloadPerson(): void {
    forkJoin({
      person: this.eventsContext.refreshPerson(),
      events: this.personEventService.getEvents(this.eventsContext.personId).pipe(catchError(() => of([] as PersonEvent[]))),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: result => {
        this.person = result.person;
        this.events = result.events;
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });
  }
}
